export default function YamlveditSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - YAML Viewer And Editor
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, troubleshooting, and contact details for YAML Viewer
            And Editor.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What is yamlvedit?
              </h3>
              <p className="mt-1">
                yamlvedit is a dedicated YAML editor for Android. View,
                edit, format, validate, and convert YAML files on your
                phone or tablet. The editor offers syntax highlighting,
                a live tree preview, find &amp; replace, and bundled
                templates for common configuration files.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What kinds of YAML files can I edit?
              </h3>
              <p className="mt-1">
                yamlvedit handles any well-formed YAML, including:
                GitHub Actions workflows
                (<code>.github/workflows/*.yml</code>), Docker Compose
                (<code>docker-compose.yml</code>), Kubernetes manifests,
                OpenAPI 3.0 specs, AWS CloudFormation, Ansible
                playbooks, GitLab CI (<code>.gitlab-ci.yml</code>),
                Spring Boot <code>application.yml</code>, and general
                application config files.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does everything work offline?
              </h3>
              <p className="mt-1">
                Yes. The parser, validator, formatter, and converters
                all run entirely on your device. Network access is used
                only for showing ads, Firebase analytics and crash
                reporting, the optional in-app update prompt, and the
                rotating cross-promotion banner.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I open a file?
              </h3>
              <p className="mt-1">
                Tap <strong>Open</strong> on the home screen and pick a
                file from the system file picker, or share a YAML file
                from another app — yamlvedit appears in the share
                sheet. You can also paste raw YAML into the editor.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Can I share a file back to other apps?
              </h3>
              <p className="mt-1">
                Yes. From the editor overflow menu (three dots), tap{" "}
                <strong>Share</strong> to send the current file via the
                system share sheet — email, chat, cloud drive, or any
                other app that accepts text or YAML files.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I save or export?
              </h3>
              <p className="mt-1">
                From the editor overflow menu: <strong>Save</strong>{" "}
                writes to the originally opened path, <strong>Save
                to device</strong> opens the system save dialog so you
                can put the file anywhere — Downloads, Drive, a cloud
                sync folder, or external storage. Use{" "}
                <strong>Convert &gt; JSON</strong> or{" "}
                <strong>Convert &gt; XML</strong> to export in another
                format.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Does it validate my YAML?
              </h3>
              <p className="mt-1">
                Yes. The editor highlights syntax errors as you type and
                shows a clear message with the line and column number.
                Use <strong>Format</strong> to auto-indent and normalise
                a file in one tap.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Why am I seeing ads?
              </h3>
              <p className="mt-1">
                Ads help support continued development. You can remove
                all ads with a one-time Pro upgrade from Settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                The app is crashing or a file won&apos;t open. What do
                I do?
              </h3>
              <p className="mt-1">
                Try closing and reopening the app. Make sure you have
                the latest version from Google Play. Clear the
                app&apos;s cache from your device&apos;s app settings
                if the problem persists. If a specific YAML file
                crashes the app, email us with the file attached.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I change the language?
              </h3>
              <p className="mt-1">
                Go to <strong>Settings &gt; Language</strong> and pick
                from 15 supported languages, including English, Spanish,
                Hindi, Bengali, Arabic, French, German, Portuguese,
                Russian, Japanese, Korean, Indonesian, Turkish, Urdu,
                and Chinese.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Contact Support
          </h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these
            details in your email:
          </p>
          <ul className="list-disc space-y-1 pl-6">
            <li>Your device model and Android OS version</li>
            <li>Your app version (found in Settings &gt; About)</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Important Links
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/yamlvedit/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/yamlvedit/privacy-policy
              </a>
            </li>
            <li>
              Support:{" "}
              <a
                href="/apps/yamlvedit/support"
                className="text-[#0F6BBF] underline"
              >
                cosmovexapps.com/apps/yamlvedit/support
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            App Information
          </h2>
          <ul className="list-disc space-y-1 pl-6">
            <li>
              <strong>App Name:</strong> YAML Viewer And Editor
            </li>
            <li>
              <strong>Package:</strong> com.cosmovex.yamlvedit
            </li>
            <li>
              <strong>Platform:</strong> Android
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
